if (!customElements.get('metric-callouts-root')) {
  const SELECTOR_CALLOUT = '[data-metric-callout]';
  const SELECTOR_VALUE = '[data-metric-value]';
  const ANIMATION_DURATION_MS = 1400;
  const INTERSECTION_OPTIONS = {
    root: null,
    threshold: 0.2,
    rootMargin: '0px 0px -10% 0px',
  };

  const easeOutQuad = (t) => t * (2 - t);

  customElements.define(
    'metric-callouts-root',
    class MetricCalloutsRoot extends HTMLElement {
      #intersectionObserver = null;
      #animationFrameId = null;

      connectedCallback() {
        this.#destroy();
        this.#bind();
      }

      disconnectedCallback() {
        this.#destroy();
      }

      #destroy() {
        if (this.#intersectionObserver) {
          this.#intersectionObserver.disconnect();
          this.#intersectionObserver = null;
        }
        if (this.#animationFrameId != null) {
          cancelAnimationFrame(this.#animationFrameId);
          this.#animationFrameId = null;
        }
      }

      #bind() {
        const animations = this.#collectAnimations();
        if (!animations.length) {
          return;
        }

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) {
          this.#applyFinalValues(animations);
          return;
        }

        this.#intersectionObserver = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) {
              continue;
            }
            if (this.#intersectionObserver) {
              this.#intersectionObserver.disconnect();
              this.#intersectionObserver = null;
            }
            this.#startCountUp(animations);
            return;
          }
        }, INTERSECTION_OPTIONS);

        this.#intersectionObserver.observe(this);
      }

      #collectAnimations() {
        const animations = [];
        for (const item of this.querySelectorAll(SELECTOR_CALLOUT)) {
          const valueEl = item.querySelector(SELECTOR_VALUE);
          if (!valueEl) {
            continue;
          }
          const target = Number.parseInt(item.getAttribute('data-metric-target'), 10);
          if (Number.isNaN(target)) {
            continue;
          }
          animations.push({ element: valueEl, target });
        }
        return animations;
      }

      #applyFinalValues(animations) {
        for (const { element, target } of animations) {
          element.textContent = String(target);
        }
      }

      #startCountUp(animations) {
        if (this.#animationFrameId != null) {
          cancelAnimationFrame(this.#animationFrameId);
          this.#animationFrameId = null;
        }

        const start = performance.now();

        const tick = (now) => {
          const elapsed = now - start;
          const progress = Math.min(1, elapsed / ANIMATION_DURATION_MS);
          const eased = easeOutQuad(progress);

          for (const { element, target } of animations) {
            element.textContent = String(Math.round(target * eased));
          }

          if (progress < 1) {
            this.#animationFrameId = requestAnimationFrame(tick);
          } else {
            this.#animationFrameId = null;
            for (const { element, target } of animations) {
              element.textContent = String(target);
            }
          }
        };

        this.#animationFrameId = requestAnimationFrame(tick);
      }
    }
  );
}
